/**
 * Stage 2 — runtime tenant selection for admin-app boot.
 *
 * Sibling of `apps/customer-app/src/lib/pickTenantConfig.ts`. Same
 * resolver, same fallback logic, same apiUrl override. Lives in this
 * app rather than a shared package because the slug→ClientConfig map is
 * app-local (each app imports its own set of `@ferro/client-*` workspace
 * packages).
 *
 * Surface-specific notes:
 *
 *   - `<slug>.admin.animalloversapp.com` (e.g. `hthd.admin.animalloversapp.com`)
 *     resolves to a tenant via `domain.surface = 'admin'`. Returns the
 *     tenant's static config.
 *
 *   - Bare `admin.animalloversapp.com` (no slug) is the platform-
 *     operator console per ADR-2026-04-28. There is no `TenantDomain`
 *     row matching that hostname, so the lookup returns null and we
 *     fall through to the ALA static config (the control-plane default
 *     brand). Auth gating for `platform_admin` role lives in
 *     `PlatformAdminRoute.tsx` — not this layer.
 *
 * #241 — boot resilience. The original implementation committed to the
 * ALA fallback on ANY by-host failure (Railway cold-start 502s, cold
 * Neon 500/503s, CORS-dropped error responses, the 2s timeout) with no
 * retry and no recovery — ~40% of loads rendered the ENTIRE admin app
 * in ALA branding on tenant subdomains. Now:
 *
 *   1. Successful lookups are cached per-hostname in localStorage
 *      (24h TTL) so repeat visits boot with the correct tenant even
 *      when the first fetch of the session races a cold start. A
 *      non-blocking background revalidation keeps the cache honest.
 *   2. RETRYABLE failures (network / timeout / 5xx / 408 / 429) retry
 *      ONCE with a tightened per-attempt timeout before first render —
 *      the blocking boot budget is capped at ~2.25s worst case (#247
 *      review: first paint must never hang on the resolver). Definitive
 *      answers (200 {tenant:null}, other 4xx) never retry — the ALA
 *      fallback is CORRECT for the bare platform console, localhost,
 *      and previews.
 *   3. If retries exhaust, we commit to the fallback so the app renders,
 *      but schedule a LATE re-fetch chain (2s/5s/15s/45s/120s). When it
 *      eventually resolves a tenant, the lookup is cached and the page
 *      reloads ONCE (per-session guard) — the reboot then applies the
 *      tenant overlay from cache instantly instead of staying
 *      wrong-branded forever. Reloads are GUARDED: never while the user
 *      has a focused input (deferred to visibilitychange/pagehide).
 *   4. If the late chain ALSO exhausts (offline boot, captive portal),
 *      a one-shot online/visibilitychange backstop re-runs the same
 *      resolve+cache+guarded-reload path when connectivity or focus
 *      returns — no session commits to the wrong brand permanently.
 *
 * T-02 / Stage 2b will replace this baked-in map with a server-
 * assembled `ClientConfig` from `Tenant` + `TenantConfig` + `TenantBranding`
 * + `TenantModuleActivation` rows.
 */

import animalLovers from '@ferro/client-animal-lovers';
import hthd from '@ferro/client-hthd';
import switchback from '@ferro/client-switchback';
import {
  resolveTenantByHostDetailed,
  type ClientConfig,
  type TenantResolveOutcome,
} from '@ferro/config';

// #427 W1 — dev-only API override: `VITE_API_URL=http://localhost:3001`
// points the dev app at a local server (the static config hardcodes prod
// Railway, which CORS-blocks localhost — previously there was NO supported
// local behavioral-verification path). Gated on import.meta.env.DEV, so
// production builds are byte-identical (dead-code eliminated). The
// override wraps the STATIC CONFIG OBJECTS themselves (`withDevApi`), not
// just PLATFORM_API_URL — several resolver branches return the static
// config directly, and patching only the constant leaves them pointed at
// prod. Sibling change in apps/customer-app/src/lib/pickTenantConfig.ts.
const DEV_API_OVERRIDE: string | null =
  import.meta.env.DEV && import.meta.env['VITE_API_URL']
    ? String(import.meta.env['VITE_API_URL'])
    : null;

function withDevApi(config: ClientConfig): ClientConfig {
  if (!DEV_API_OVERRIDE) return config;
  return {
    ...config,
    deployment: { ...config.deployment, apiUrl: DEV_API_OVERRIDE },
  };
}

const ala = withDevApi(animalLovers);

const PLATFORM_API_URL = ala.deployment.apiUrl;

const TENANT_CONFIGS: Record<string, ClientConfig> = {
  'animal-lovers': ala,
  'hthd': withDevApi(hthd),
  // Standing demo tenant (fictional business — sales demos / onboarding
  // walkthroughs). See clients/switchback/config.ts header.
  'switchback': withDevApi(switchback),
};

// #241/#247 — ONE boot retry after the initial try, with a tightened
// per-attempt timeout: the blocking first-paint budget is capped at
// ~2.25s worst case (2 × 1000ms timeouts + 250ms backoff). The #247
// review rejected the previous [250,750,1500] × 2s schedule (~10.5s
// blank page on a dark API); the dropped retries now live in the late
// chain below, which starts at 2s instead of 5s.
const RETRY_DELAYS_MS: readonly number[] = [250];
// #247 — per-attempt timeout on the BLOCKING boot path only. The
// non-blocking paths (late chain, revalidation, backstop) use the
// resolver's full 2s default so they ride out slow cold-start replies.
const BOOT_TIMEOUT_MS = 1000;
const LATE_TIMEOUT_MS = 2000;
// #241 — late re-fetch schedule once the fallback has rendered. Railway
// Fluid-Compute cold starts resolve well inside this window.
const LATE_RETRY_DELAYS_MS: readonly number[] = [
  2_000, 5_000, 15_000, 45_000, 120_000,
];
const LOOKUP_CACHE_PREFIX = 'ferro:tenant-lookup:';
const LOOKUP_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h
const LATE_RELOAD_GUARD_KEY = 'ferro:tenant-late-reload-done';

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export interface PickTenantConfigOptions {
  fetchFn?: typeof fetch;
  timeoutMs?: number;
  apiBaseUrl?: string;
  /** Override the hostname sent to the API (test/dev). Defaults to window.location.hostname. */
  host?: string;
  /** #241 — backoff between boot retries. Pass [] to disable retries. */
  retryDelaysMs?: readonly number[];
  /** #241 — late re-fetch schedule after committing to the fallback. Pass [] to disable. */
  lateRetryDelaysMs?: readonly number[];
  /** #241 — storage seam for the lookup cache (default: localStorage). */
  storage?: StorageLike | null;
  /** #241 — session-scoped storage seam for the reload-once guard (default: sessionStorage). */
  sessionStore?: StorageLike | null;
  /** #241 — reload seam invoked when the late re-fetch resolves a tenant. */
  reloadFn?: () => void;
  /** #241 — sleep seam for tests. */
  sleepFn?: (ms: number) => Promise<void>;
  /** #241 — observability hook: late re-fetch resolved `slug`. */
  onLateResolve?: (slug: string) => void;
  /** #241 — disable the non-blocking cache revalidation (tests). */
  revalidateCache?: boolean;
  /** #247 — guarded-reload probe seam (default: document.visibilityState === 'hidden'). */
  isDocumentHidden?: () => boolean;
  /** #247 — guarded-reload probe seam (default: document.activeElement is INPUT/TEXTAREA/contenteditable). */
  isEditingFocused?: () => boolean;
}

interface CachedLookup {
  slug: string;
  cachedAt: number;
}

function safeLocalStorage(): StorageLike | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch {
    return null;
  }
}

function safeSessionStorage(): StorageLike | null {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : null;
  } catch {
    return null;
  }
}

function lookupCacheKey(host: string): string {
  return `${LOOKUP_CACHE_PREFIX}${host}`;
}

function readLookupCache(storage: StorageLike, host: string): string | null {
  try {
    const raw = storage.getItem(lookupCacheKey(host));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CachedLookup>;
    if (typeof parsed.slug !== 'string' || typeof parsed.cachedAt !== 'number') {
      return null;
    }
    if (Date.now() - parsed.cachedAt > LOOKUP_CACHE_TTL_MS) return null;
    return parsed.slug;
  } catch {
    return null;
  }
}

function writeLookupCache(storage: StorageLike, host: string, slug: string): void {
  try {
    const payload: CachedLookup = { slug, cachedAt: Date.now() };
    storage.setItem(lookupCacheKey(host), JSON.stringify(payload));
  } catch {
    // Storage full or disabled — boot still works, just without the fast path.
  }
}

function clearLookupCache(storage: StorageLike, host: string): void {
  try {
    storage.removeItem(lookupCacheKey(host));
  } catch {
    // ignore
  }
}

function withPlatformApiUrl(cfg: ClientConfig): ClientConfig {
  return {
    ...cfg,
    deployment: {
      ...cfg.deployment,
      apiUrl: PLATFORM_API_URL,
    },
  };
}

const defaultSleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * #247 — clear the reload-once guard whenever a boot resolves a tenant
 * successfully (cache fast path or network). Without this, a long-lived
 * tab that used its one late reload could never recover from a SECOND
 * in-session cold start when localStorage is unavailable.
 */
function clearLateReloadGuard(sessionStore: StorageLike | null): void {
  try {
    sessionStore?.removeItem(LATE_RELOAD_GUARD_KEY);
  } catch {
    // ignore
  }
}

interface ReloadGuardProbes {
  isDocumentHidden: () => boolean;
  isEditingFocused: () => boolean;
}

function defaultIsDocumentHidden(): boolean {
  return (
    typeof document !== 'undefined' && document.visibilityState === 'hidden'
  );
}

function defaultIsEditingFocused(): boolean {
  if (typeof document === 'undefined') return false;
  const el = document.activeElement;
  if (!el) return false;
  if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') return true;
  return (el as HTMLElement).isContentEditable === true;
}

/**
 * #247 — guarded reload. An unguarded window.location.reload() up to
 * ~3 minutes after first paint can wipe in-progress login/registration
 * input mid-keystroke. Policy: hidden tab → reload immediately (the
 * user isn't looking); visible and not typing → reload now; visible
 * with a focused INPUT/TEXTAREA/contenteditable → DEFER until the tab
 * is hidden (visibilitychange) or the page is leaving anyway (pagehide).
 */
function performGuardedReload(
  reloadFn: () => void,
  probes: ReloadGuardProbes,
): void {
  if (probes.isDocumentHidden() || !probes.isEditingFocused()) {
    reloadFn();
    return;
  }
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    reloadFn();
    return;
  }
  let fired = false;
  const fire = (): void => {
    if (fired) return;
    fired = true;
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', fire);
    reloadFn();
  };
  const onVisibility = (): void => {
    if (probes.isDocumentHidden()) fire();
  };
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', fire);
}

interface ResolveEnv {
  apiBaseUrl: string;
  fetchFn?: typeof fetch;
  timeoutMs?: number;
  host?: string;
}

/**
 * Non-blocking cache revalidation: confirm (or correct) the cached
 * slug for the next boot. Never reloads mid-session — a stale brand for
 * one navigation is the documented acceptable posture (see
 * packages/config resolve-tenant SECURITY NOTE).
 */
async function revalidateLookupCache(
  env: ResolveEnv,
  storage: StorageLike,
  host: string,
  cachedSlug: string,
): Promise<void> {
  const outcome = await resolveTenantByHostDetailed(env);
  if (!outcome.ok) return; // transient — keep the cache.
  const slug = outcome.tenant?.slug;
  if (slug && TENANT_CONFIGS[slug]) {
    if (slug !== cachedSlug) {
      // eslint-disable-next-line no-console
      console.warn(
        `[pickTenantConfig] cached tenant '${cachedSlug}' superseded by '${slug}' — corrected for next boot`,
      );
    }
    writeLookupCache(storage, host, slug);
    return;
  }
  // Definitive "no tenant for this host" — the cache is stale.
  clearLookupCache(storage, host);
}

interface LateRefetchArgs {
  env: ResolveEnv;
  delays: readonly number[];
  sleep: (ms: number) => Promise<void>;
  storage: StorageLike | null;
  sessionStore: StorageLike | null;
  host: string | undefined;
  reloadFn: () => void;
  probes: ReloadGuardProbes;
  onLateResolve?: (slug: string) => void;
}

/**
 * Shared outcome handling for the late chain and the post-exhaustion
 * backstop. Returns 'continue' ONLY for retryable failures; every other
 * outcome (tenant committed, definitive answer, contract break)
 * terminates the recovery flow.
 */
function handleLateOutcome(
  args: LateRefetchArgs,
  outcome: TenantResolveOutcome,
): 'continue' | 'stop' {
  if (!outcome.ok) {
    return outcome.retryable ? 'continue' : 'stop';
  }
  const slug = outcome.tenant?.slug;
  if (!slug || !TENANT_CONFIGS[slug] || slug === ala.id) {
    // Definitive answer matching what we already rendered — done.
    return 'stop';
  }
  if (args.host && args.storage) {
    writeLookupCache(args.storage, args.host, slug);
  }
  args.onLateResolve?.(slug);
  // Reload-once guard: the rebooted page resolves from cache, so a
  // second reload should never be needed; the guard protects
  // against a loop when storage is unavailable.
  try {
    if (args.sessionStore?.getItem(LATE_RELOAD_GUARD_KEY) === '1') {
      return 'stop';
    }
    args.sessionStore?.setItem(LATE_RELOAD_GUARD_KEY, '1');
  } catch {
    // ignore — still reload once.
  }
  // eslint-disable-next-line no-console
  console.warn(
    `[pickTenantConfig] late tenant resolution succeeded (slug=${slug}); reloading to apply the tenant overlay`,
  );
  performGuardedReload(args.reloadFn, args.probes);
  return 'stop';
}

/**
 * #247 — post-exhaustion recovery backstop. A tab that boots offline /
 * behind a captive portal can outlast the whole late chain; without
 * this, it stays wrong-branded for the entire session. One-shot
 * `online` + visible-only `visibilitychange` listeners re-run the same
 * resolve+cache+guarded-reload path when connectivity or focus returns.
 */
function registerRecoveryBackstop(args: LateRefetchArgs): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  let used = false;
  const detach = (): void => {
    window.removeEventListener('online', onOnline);
    document.removeEventListener('visibilitychange', onVisibility);
  };
  const attempt = (): void => {
    if (used) return;
    used = true;
    detach();
    void (async () => {
      const outcome = await resolveTenantByHostDetailed(args.env);
      handleLateOutcome(args, outcome);
    })();
  };
  const onOnline = (): void => attempt();
  const onVisibility = (): void => {
    if (!args.probes.isDocumentHidden()) attempt();
  };
  window.addEventListener('online', onOnline);
  document.addEventListener('visibilitychange', onVisibility);
}

/**
 * #241 — late re-fetch chain. Fire-and-forget: runs after the boot
 * promise already resolved with the fallback config. On the first
 * successful lookup that maps to a known tenant, cache it and reload
 * once (guarded — see performGuardedReload) so the reboot applies the
 * tenant overlay (from cache) instead of the surface staying
 * ALA-branded forever. If every attempt fails retryably, hand off to
 * the one-shot online/visibilitychange backstop.
 */
function scheduleLateTenantRefetch(args: LateRefetchArgs): void {
  if (args.delays.length === 0) return;
  void (async () => {
    for (const delay of args.delays) {
      await args.sleep(delay);
      const outcome = await resolveTenantByHostDetailed(args.env);
      if (handleLateOutcome(args, outcome) === 'stop') return;
    }
    registerRecoveryBackstop(args);
  })();
}

export async function pickTenantConfig(
  opts: PickTenantConfigOptions = {},
): Promise<ClientConfig> {
  const host =
    opts.host ?? (typeof window !== 'undefined' ? window.location.hostname : undefined);
  // #247 — the blocking boot path uses the tightened timeout; the
  // non-blocking paths (revalidation, late chain, backstop) keep the
  // resolver's full window so they ride out slow cold-start replies.
  const env: ResolveEnv = {
    apiBaseUrl: opts.apiBaseUrl ?? PLATFORM_API_URL,
    fetchFn: opts.fetchFn,
    timeoutMs: opts.timeoutMs ?? BOOT_TIMEOUT_MS,
    host,
  };
  const lateEnv: ResolveEnv = {
    ...env,
    timeoutMs: opts.timeoutMs ?? LATE_TIMEOUT_MS,
  };
  const storage = opts.storage === undefined ? safeLocalStorage() : opts.storage;
  const sessionStore =
    opts.sessionStore === undefined ? safeSessionStorage() : opts.sessionStore;
  const sleep = opts.sleepFn ?? defaultSleep;
  const retryDelays = opts.retryDelaysMs ?? RETRY_DELAYS_MS;
  const lateDelays = opts.lateRetryDelaysMs ?? LATE_RETRY_DELAYS_MS;
  const probes: ReloadGuardProbes = {
    isDocumentHidden: opts.isDocumentHidden ?? defaultIsDocumentHidden,
    isEditingFocused: opts.isEditingFocused ?? defaultIsEditingFocused,
  };

  // #241 (1) — cache fast path: boot with the last-known tenant for this
  // hostname so cold starts can't wrong-brand repeat visits.
  if (host && storage) {
    const cachedSlug = readLookupCache(storage, host);
    if (cachedSlug) {
      const cached = TENANT_CONFIGS[cachedSlug];
      if (cached) {
        if (opts.revalidateCache !== false) {
          void revalidateLookupCache(lateEnv, storage, host, cachedSlug);
        }
        clearLateReloadGuard(sessionStore);
        return withPlatformApiUrl(cached);
      }
    }
  }

  // #241 (2) — resolve with bounded retry on RETRYABLE failures only.
  let attempt = 0;
  for (;;) {
    const outcome = await resolveTenantByHostDetailed(env);
    if (outcome.ok) {
      // Successful boot resolution — release the reload-once guard so a
      // FUTURE in-session cold start can still recover via late reload.
      clearLateReloadGuard(sessionStore);
      const slug = outcome.tenant?.slug;
      const cfg = slug ? TENANT_CONFIGS[slug] : undefined;
      if (slug && cfg) {
        if (host && storage) writeLookupCache(storage, host, slug);
        return withPlatformApiUrl(cfg);
      }
      // Definitive: control-plane default (bare admin console, previews,
      // localhost, or an unknown slug). Correct fallback — no retry.
      return ala;
    }
    const delay = attempt < retryDelays.length ? retryDelays[attempt] : undefined;
    if (!outcome.retryable || delay === undefined) break;
    await sleep(delay);
    attempt += 1;
  }

  // #241 (3) — retries exhausted on a transient failure: render the
  // fallback so the app isn't blank, but keep trying in the background
  // and apply the tenant overlay (via cached reboot) when it lands.
  scheduleLateTenantRefetch({
    env: lateEnv,
    delays: lateDelays,
    sleep,
    storage,
    sessionStore,
    host,
    reloadFn:
      opts.reloadFn ??
      (() => {
        if (typeof window !== 'undefined') window.location.reload();
      }),
    probes,
    onLateResolve: opts.onLateResolve,
  });

  // Bridge: ALA static config = control-plane default brand. Used for
  // bare `admin.animalloversapp.com` (the platform-operator console)
  // and for any unrecognized host.
  return ala;
}
